/**
 * AT SEA — what this device can do without a signal, measured on the spot.
 *
 * It lives under Progress because Progress asks "what can you actually do", and this is the same
 * question with the network taken away. A candidate should meet this IN PORT, while there is still
 * something he can do about the answer.
 *
 * NOTHING HERE IS REMEMBERED. Every figure is read from the cache at the moment it is shown (see
 * prepare.mjs). A stored "prepared: true" would be a claim, and it fails in precisely the case it
 * exists for: an evicted cache, a changed URL, a download that died on port wifi. The flag would
 * still be green and the cadet would find out a thousand miles out.
 *
 * The refusals are given equal room to the capabilities. A surface that lists what you HAVE and goes
 * quiet about what you have not is how somebody sails believing he has the written explanations
 * aboard.
 */
import { useCallback, useEffect, useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { offlinePlan, prepareForSea, seaReadiness } from './offline/prepare.mjs';

const kb = (n) => (n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);

export default function SeaReadiness() {
  const [plan, setPlan] = useState(null);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);         // { done, total, bytes, failures }
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const measure = useCallback(async (p) => {
    try { setState(await seaReadiness(p)); } catch (e) { setError(String(e.message || e)); }
  }, []);

  useEffect(() => {
    let live = true;
    offlinePlan()
      .then((p) => { if (!live) return; setPlan(p); return measure(p); })
      .catch((e) => { if (live) setError(String(e.message || e)); });
    return () => { live = false; };
  }, [measure]);

  const run = async () => {
    setResult(null); setError(null);
    setBusy({ done: 0, total: 0, bytes: 0, failures: 0 });
    try {
      const p = plan || await offlinePlan();
      if (!plan) setPlan(p);
      const r = await prepareForSea(p, { onProgress: setBusy });
      setResult(r);
      await measure(p);
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(null); }
  };

  if (error && !state) return (
    <div className="g-glass shell-panel">
      <div className="g-label">At sea</div>
      <p className="g-body" style={{ marginTop: 'var(--g-2)' }}>
        <Value of={refused(`cannot tell you what is aboard: ${error}`)} />
      </p>
    </div>
  );

  return (
    <div className="g-glass shell-panel">
      <div className="g-label">At sea</div>
      <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>
        What works with no signal
      </h2>

      {/* THE ONE THING THAT IS TRUE WHATEVER ELSE IS MISSING. It is stated first because it is the
          reason the app is worth opening at sea at all. */}
      <p className="g-body sea-always">
        <b>Sight reduction always works.</b> The almanac is not downloaded — the VSOP87 series travel
        inside the app and your phone computes the sky for any date, with no expiry. Nothing you do
        on this screen can take that away, and nothing you fail to download can either.
      </p>

      {!state ? <div className="gl-empty g-body">Reading what is on this device…</div> : (
        <>
          <div className="sea-rows">
            <div className="sea-row">
              <span className="sea-what">The app itself</span>
              <Value of={state.shell
                ? computed('aboard — it will open with no signal')
                : refused('NOT aboard — open this page once on wifi and it stores itself')} />
            </div>
            {state.groups.map((g) => (
              <div key={g.id} className="sea-row">
                <span className="sea-what">{g.label}</span>
                <Value of={g.have === g.want
                  ? computed(`all ${g.want} aboard`)
                  : g.have === 0
                    ? refused(`none of ${g.want} — needs a signal`)
                    : computed(`${g.have} of ${g.want} aboard`)} />
              </div>
            ))}
          </div>

          {/* Counted, never rounded up. */}
          <p className="g-body g-dim" style={{ marginTop: 'var(--g-3)' }}>
            {state.have} of {state.want} downloadable items are on this device.
            {state.partial && ' A partial download is shown as partial rather than reported as ready.'}
          </p>
        </>
      )}

      {busy ? (
        <div className="sea-busy">
          <div className="g-label">Downloading</div>
          <p className="g-body">
            {busy.done} of {busy.total || '…'} · <b>{kb(busy.bytes)}</b> stored
            {busy.failures ? ` · ${busy.failures} failed` : ''}
          </p>
          {/* The bar is the COUNT, not a guess at time remaining. */}
          <div className="sea-bar"><div className="sea-bar-in"
            style={{ width: busy.total ? `${Math.round((busy.done / busy.total) * 100)}%` : '4%' }} /></div>
        </div>
      ) : (
        <button className="g-btn g-btn-primary sea-go" onClick={run}>
          {state?.ready ? 'Download again' : 'Prepare for sea'}
        </button>
      )}

      {result && (
        <p className="g-body" style={{ marginTop: 'var(--g-3)' }}>
          <Value of={result.failures.length
            ? refused(`${result.stored} of ${result.total} stored · ${kb(result.bytes)} · `
                    + `${result.failures.length} could not be fetched: ${result.failures[0].url} `
                    + `(${result.failures[0].why}). Run it again on a better connection.`)
            /* "stored", not "downloaded" — this is the decoded size sitting on the device. The
               responses arrive compressed and the wire cost is several times less, but fetch does
               not expose it, so the smaller number is not claimed. */
            : computed(`${result.stored} of ${result.total} stored · ${kb(result.bytes)} on this `
                     + 'device (less than that over the air — it arrives compressed)')} />
        </p>
      )}
      {error && state && (
        <p className="g-body" style={{ marginTop: 'var(--g-2)' }}><Value of={refused(error)} /></p>
      )}

      {/* WHAT CANNOT COME ABOARD — the same size type as everything else, because a candidate who
          skims this screen must not come away thinking he has it all. */}
      {plan?.never?.length ? (
        <div className="sea-never">
          <div className="g-label">What stays ashore</div>
          {plan.never.map((n) => (
            <p key={n.what} className="g-body">
              <b>{n.what}</b> — {n.why}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
